import React, { useRef, useEffect } from 'react';

interface WaveformVisualizerProps {
  analyserNode: AnalyserNode;
  className?: string;
}

const WaveformVisualizer: React.FC<WaveformVisualizerProps> = ({ analyserNode, className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    // Read the brand cyan from CSS at draw time so light/dark modes both pick up
    // the right token (`--primary` lifts in dark per DESIGN_SYSTEM_COLOR.md §4.3).
    // Cached per-frame in `getStroke` rather than per-effect so a runtime theme
    // toggle re-resolves on the next frame.
    const getStroke = () => {
      const hsl = getComputedStyle(canvas).getPropertyValue('--primary').trim();
      return hsl ? `hsl(${hsl})` : '#1FA5DC';
    };

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyserNode.getByteTimeDomainData(dataArray);

      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      ctx.lineWidth = 2;
      ctx.strokeStyle = getStroke();
      ctx.beginPath();

      const sliceWidth = width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * height) / 2;
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
        x += sliceWidth;
      }

      ctx.lineTo(width, height / 2);
      ctx.stroke();
    };

    draw();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [analyserNode]);

  return (
    <canvas
      ref={canvasRef}
      width={200}
      height={40}
      className={className}
    />
  );
};

export default WaveformVisualizer;
